import {
  FC,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useEffectEvent,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import { RootState } from 'src/redux/rootReducerType';
import { AppDispatch } from 'src/redux/store';
import { upsertFormSignatureRequest } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import { setSavedInitials } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import axios from 'src/utils/axios';
import { getTimeZone } from 'src/utils/signatureUtils';
import { safelyFetchDocument } from 'src/components/DocumenManagerV2/utils';
import type {
  FormSignatureRequestType,
  IndividualSignatureRequestType,
} from 'src/types/SignatureTypes';
import { parseXfdfPreview } from './xfdfPreview';
import PdfPageRenderer from './PdfPageRenderer';
import SignatureBoxOverlay from './SignatureBoxOverlay';
import SignaturePadModal from './SignaturePadModal';
import ZoomControls from '../shared/ZoomControls';
import type { SignatureResult } from './SignaturePadModal';
import {
  buildInkXfdf,
  buildFreetextXfdf,
  buildStandaloneInkXfdf,
  buildStandaloneFreetextXfdf,
} from './xfdfSerializer';
import { isUserTenantSignature } from './signatureFilters';
import { cacheBustPdfUrl } from 'src/utils/signatureUtils';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/static/pdf.worker.min.mjs';

interface PdfSignViewerProps {
  formSignatureRequest: FormSignatureRequestType;
  userId: number;
  readOnly: boolean;
  onSavingChange?: (saving: boolean) => void;
  onSignError?: () => void;
  ref?: Ref<PdfSignViewerHandle>;
}

export interface PdfSignViewerHandle {
  signAll: (autoSign: boolean) => void;
  hasSavedSignature: () => boolean;
}

interface MappedSignature {
  id: number;
  x: number;
  y: number;
  width: number;
  height: number;
  pageNumber: number;
  completed: boolean;
  isInitials?: boolean;
}

type SignedMapEntry = {
  ink?: { viewBox: string; paths: string[] };
  text?: { text: string; fontFamily: string };
};

function filterUserSignatures(
  sigs: IndividualSignatureRequestType[],
  userId: number
): IndividualSignatureRequestType[] {
  return sigs.filter((sig) => isUserTenantSignature(sig, userId));
}

async function saveSavedSignature(signatureData: string): Promise<void> {
  await axios.put('signature/saved_signature/', {
    signature_data: signatureData,
  });
}

function parseSavedInkStrokes(savedXfdf: string): {
  strokes: { x: number; y: number }[][];
  savedW: number;
  savedH: number;
} | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(savedXfdf, 'text/xml');
  const gestures = doc.querySelectorAll('gesture');
  const rect = doc.querySelector('ink')?.getAttribute('rect');
  if (!rect || gestures.length === 0) {
    return null;
  }

  const [rx1, ry1, rx2, ry2] = rect.split(',').map(Number);
  const savedW = rx2 - rx1;
  const savedH = ry2 - ry1;

  const strokes: { x: number; y: number }[][] = [];
  gestures.forEach((g) => {
    const raw = g.textContent?.trim();
    if (!raw) {
      return;
    }
    const points = raw.split(';').map((pair) => {
      const [px, py] = pair.split(',').map(Number);
      return { x: px - rx1, y: savedH - (py - ry1) };
    });
    strokes.push(points);
  });

  return { strokes, savedW, savedH };
}

const PdfSignViewer: FC<PdfSignViewerProps> = ({
  formSignatureRequest,
  userId,
  readOnly,
  onSavingChange,
  onSignError,
  ref,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const displayName =
    useSelector(
      (state: RootState) => state.newRenterInterfaceUser.user?.displayName
    ) ?? '';

  const savedSignatureXfdf = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.savedSignatureXfdf
  );

  const savedInitialsText = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.savedInitialsText
  );

  const savedInitialsFont = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.savedInitialsFont
  );

  const savedInitialsXfdf = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.savedInitialsXfdf
  );

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [pages, setPages] = useState<PDFPageProxy[]>([]);
  const [zoom, setZoom] = useState(1);
  const [fitScale, setFitScale] = useState(1);
  const [signedMap, setSignedMap] = useState<Record<number, SignedMapEntry>>(
    {}
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [signAllPending, setSignAllPending] = useState(false);
  const [justCollectedSignature, setJustCollectedSignature] = useState(false);
  const [justCollectedInitials, setJustCollectedInitials] = useState(false);
  const [pendingSignatureXfdf, setPendingSignatureXfdf] = useState<
    string | null
  >(null);
  const [pendingInitialsData, setPendingInitialsData] = useState<{
    text: string;
    font: string;
  } | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [activeFsr, setActiveFsr] =
    useState<FormSignatureRequestType>(formSignatureRequest);

  const containerRef = useRef<HTMLDivElement>(null);
  const pdfDocRef = useRef<PDFDocumentProxy | null>(null);

  useEffect(() => {
    onSavingChange?.(isSaving);
  }, [isSaving, onSavingChange]);

  const mappedSignatures: MappedSignature[] = useMemo(() => {
    if (readOnly) {
      return [];
    }
    const userSigs = filterUserSignatures(
      activeFsr.needed_signatures ?? [],
      userId
    );

    return userSigs
      .filter(
        (sig) =>
          sig.certification_page &&
          typeof sig.certification_page === 'object' &&
          sig.x != null &&
          sig.y != null &&
          sig.width != null &&
          sig.height != null
      )
      .map((sig) => ({
        id: sig.id,
        x: Number(sig.x),
        y: Number(sig.y),
        width: Number(sig.width),
        height: Number(sig.height),
        pageNumber: sig.certification_page.order + 1,
        completed: sig.signature_completed,
        isInitials: sig.is_initials,
      }));
  }, [activeFsr, userId, readOnly]);

  const pendingSignatures = useMemo(
    () => mappedSignatures.filter((s) => !s.completed),
    [mappedSignatures]
  );

  const getCurrentFsr = useEffectEvent(() => formSignatureRequest);

  useEffect(() => {
    let cancelled = false;

    const loadPdf = async (): Promise<void> => {
      setIsSaving(false);
      setLoadError(false);
      setLoading(true);
      setPages([]);

      let fsrToUse = getCurrentFsr();

      if (!readOnly) {
        try {
          const params = new URLSearchParams({
            expand: [
              'certification_document.pages',
              'needed_signatures.household_member',
              'needed_signatures.certification_page',
              'needed_signatures.signature_mapping',
              'needed_signatures.signature_mapping.sit',
            ].join(','),
            certification_document__is_deleted: 'false',
          });

          const response = await axios.get<FormSignatureRequestType>(
            `signature/signature_request_form/${
              formSignatureRequest.id
            }/?${params.toString()}`
          );

          if (!cancelled) {
            fsrToUse = response.data;
            setActiveFsr(response.data);

            const certDocId =
              typeof response.data.certification_document === 'object'
                ? (response.data.certification_document as { id: number }).id
                : response.data.certification_document;

            dispatch(
              upsertFormSignatureRequest({
                certificationId: response.data.certification,
                certDocId: certDocId,
                fsr: {
                  id: response.data.id,
                  signed_form: response.data.signed_form || null,
                  generated_form_url: response.data.generated_form_url || null,
                  needed_signatures: response.data.needed_signatures ?? [],
                },
              })
            );
          }
        } catch (error) {
          console.error('Failed to fetch FSR for T&C generation:', error);
        }
      }

      const fileUrl = cacheBustPdfUrl(
        fsrToUse.signed_form || fsrToUse.generated_form_url
      );

      if (!fileUrl) {
        setLoading(false);
        return;
      }

      try {
        const blob = await safelyFetchDocument(fileUrl, 3);
        if (cancelled) {
          return;
        }

        const arrayBuffer = await blob.arrayBuffer();
        if (cancelled) {
          return;
        }

        const doc = await pdfjsLib.getDocument({
          data: arrayBuffer,
          wasmUrl: '/static/wasm/',
        }).promise;
        if (cancelled) {
          doc.cleanup();
          return;
        }

        pdfDocRef.current = doc;

        const loadedPages = await Promise.all(
          Array.from({ length: doc.numPages }, (_, i) => doc.getPage(i + 1))
        );
        setPages(loadedPages);

        const minDisplayTime = 500;
        setTimeout(() => {
          if (!cancelled) {
            setLoading(false);
          }
        }, minDisplayTime);
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setLoadError(true);
          setLoading(false);
        }
      }
    };

    void loadPdf();

    return () => {
      cancelled = true;
      if (pdfDocRef.current) {
        pdfDocRef.current.cleanup();
        pdfDocRef.current = null;
      }
    };
  }, [formSignatureRequest.id, readOnly, retryCount, dispatch]);

  useEffect(() => {
    if (!containerRef.current || pages.length === 0) {
      return;
    }
    const container = containerRef.current;
    const firstPage = pages[0];
    const baseViewport = firstPage.getViewport({ scale: 1 });

    const updateScale = (): void => {
      const containerWidth = container.clientWidth;
      const computedFit = (containerWidth - 16) / baseViewport.width;
      setFitScale(Math.max(computedFit, 0.5));
    };

    updateScale();

    const observer = new ResizeObserver(updateScale);
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [pages]);

  const effectiveScale = useMemo(() => fitScale * zoom, [fitScale, zoom]);

  const getPageHeight = useCallback(
    (pageNumber: number): number => {
      const pdfPage = pages[pageNumber - 1];
      return pdfPage ? pdfPage.getViewport({ scale: 1 }).height : 792;
    },
    [pages]
  );

  const buildXfdfForSig = useCallback(
    (
      sig: MappedSignature,
      result: SignatureResult
    ): { xfdf: string; standaloneXfdf: string | null } => {
      const pgHeight = getPageHeight(sig.pageNumber);
      const targetRect = {
        x: sig.x,
        y: sig.y,
        width: sig.width,
        height: sig.height,
      };
      const customData = { signatureId: sig.id, willBeCreated: true };

      if (result.type === 'ink') {
        return {
          xfdf: buildInkXfdf(
            result.strokes,
            result.canvasWidth,
            result.canvasHeight,
            targetRect,
            sig.pageNumber - 1,
            pgHeight,
            displayName,
            customData
          ),
          standaloneXfdf: buildStandaloneInkXfdf(result.strokes, displayName),
        };
      }

      return {
        xfdf: buildFreetextXfdf(
          result.text,
          result.fontFamily,
          targetRect,
          sig.pageNumber - 1,
          pgHeight,
          displayName,
          customData
        ),
        standaloneXfdf: buildStandaloneFreetextXfdf(
          result.text,
          result.fontFamily,
          displayName
        ),
      };
    },
    [getPageHeight, displayName]
  );

  const buildXfdfFromSaved = useCallback(
    (sig: MappedSignature, savedXfdf: string): string | null => {
      const parsed = parseXfdfPreview(savedXfdf);
      const pgHeight = getPageHeight(sig.pageNumber);
      const targetRect = {
        x: sig.x,
        y: sig.y,
        width: sig.width,
        height: sig.height,
      };
      const customData = { signatureId: sig.id, willBeCreated: true };

      if (parsed?.type === 'ink') {
        const inkData = parseSavedInkStrokes(savedXfdf);
        if (!inkData) {
          return null;
        }
        return buildInkXfdf(
          inkData.strokes,
          inkData.savedW,
          inkData.savedH,
          targetRect,
          sig.pageNumber - 1,
          pgHeight,
          displayName,
          customData
        );
      }

      if (parsed?.type === 'text') {
        return buildFreetextXfdf(
          parsed.text,
          parsed.fontFamily,
          targetRect,
          sig.pageNumber - 1,
          pgHeight,
          displayName,
          customData
        );
      }

      return null;
    },
    [getPageHeight, displayName]
  );

  const previewForXfdf = useCallback(
    (xfdf: string, result?: SignatureResult): SignedMapEntry => {
      const preview = parseXfdfPreview(xfdf);
      if (preview?.type === 'ink') {
        return { ink: { viewBox: preview.viewBox, paths: preview.paths } };
      }
      if (preview?.type === 'text') {
        return {
          text: { text: preview.text, fontFamily: preview.fontFamily },
        };
      }
      if (result?.type === 'text') {
        return {
          text: { text: result.text, fontFamily: result.fontFamily },
        };
      }
      return {};
    },
    []
  );

  const postBatchSignatures = useCallback(
    async (
      items: Array<{
        id: number;
        xfdf: string;
        pageNumber: number;
      }>,
      initialsText?: string,
      initialsFontFamily?: string
    ): Promise<void> => {
      const signatures = await Promise.all(
        items.map(async (item) => {
          const pdfPage = pages[item.pageNumber - 1];
          const viewport = pdfPage
            ? pdfPage.getViewport({ scale: 1 })
            : { width: 612, height: 792 };
          return {
            id: item.id,
            xfdf_string: item.xfdf,
            page_width: viewport.width,
            page_height: viewport.height,
          };
        })
      );

      await axios.post('signature/signature_request_individual/batch_sign/', {
        signatures,
        sign_timezone: getTimeZone(),
        initials_text: initialsText || '',
        initials_font_family: initialsFontFamily || 'Dancing Script',
      });
    },
    [pages]
  );

  const submitBatch = useCallback(
    async (
      sigs: MappedSignature[],
      xfdfMap: Record<number, string>,
      standaloneXfdf: string | null,
      result?: SignatureResult,
      initialsText?: string,
      initialsFontFamily?: string
    ): Promise<void> => {
      const optimistic: Record<number, SignedMapEntry> = {};

      // Build preview for each signature
      const userSigs = filterUserSignatures(
        activeFsr.needed_signatures ?? [],
        userId
      );

      for (const sig of sigs) {
        const isr = userSigs.find((i) => i.id === sig.id);

        if (isr && isr.is_initials && initialsText) {
          // For typed initials, use the text preview
          optimistic[sig.id] = {
            text: {
              text: initialsText,
              fontFamily: initialsFontFamily || 'Dancing Script',
            },
          };
        } else {
          // For signatures and drawn initials, use XFDF preview
          optimistic[sig.id] = previewForXfdf(xfdfMap[sig.id], result);
        }
      }

      setSignedMap((prev) => ({ ...prev, ...optimistic }));
      setIsSaving(true);

      try {
        const items = sigs.map((sig) => ({
          id: sig.id,
          xfdf: xfdfMap[sig.id],
          pageNumber: sig.pageNumber,
        }));
        await postBatchSignatures(items, initialsText, initialsFontFamily);

        setRetryCount((c) => c + 1);

        if (standaloneXfdf) {
          try {
            await saveSavedSignature(standaloneXfdf);
          } catch {
            // Non-critical
          }
        }
      } catch {
        setSignedMap((prev) => {
          const next = { ...prev };
          for (const sig of sigs) {
            delete next[sig.id];
          }
          return next;
        });
        onSignError?.();
      } finally {
        setIsSaving(false);
      }
    },
    [
      previewForXfdf,
      postBatchSignatures,
      onSignError,
      activeFsr,
      userId,
      filterUserSignatures,
    ]
  );

  const applyAutoSignAllWithInitials = useCallback(async (): Promise<void> => {
    if (pendingSignatures.length === 0) {
      return;
    }

    const userSigs = filterUserSignatures(
      activeFsr.needed_signatures ?? [],
      userId
    );
    const pendingIsrs = userSigs.filter((sig) =>
      pendingSignatures.some((ps) => ps.id === sig.id)
    );

    const signatureIsrs = pendingIsrs.filter((sig) => !sig.is_initials);
    const initialsIsrs = pendingIsrs.filter((sig) => sig.is_initials);

    // Check what we need vs what we have
    const needsSignature = signatureIsrs.length > 0;
    const needsInitials = initialsIsrs.length > 0;
    const hasSignature = !!savedSignatureXfdf;
    const hasInitials = !!savedInitialsText || !!savedInitialsXfdf;

    // Determine if we need to open modal
    let shouldOpenModal = false;

    if (needsSignature && !hasSignature) {
      shouldOpenModal = true;
    }
    if (needsInitials && !hasInitials) {
      shouldOpenModal = true;
    }

    if (shouldOpenModal) {
      // Open modal to collect missing signature/initials
      setSignAllPending(true);
      setModalOpen(true);
      return;
    }

    const xfdfMap: Record<number, string> = {};

    // Build XFDF for signatures and initials
    for (const sig of pendingSignatures) {
      const isr = pendingIsrs.find((i) => i.id === sig.id);
      if (isr && !isr.is_initials && savedSignatureXfdf) {
        const xfdf = buildXfdfFromSaved(sig, savedSignatureXfdf);
        if (!xfdf) {
          return;
        }
        xfdfMap[sig.id] = xfdf;
      } else if (isr && isr.is_initials) {
        // For initials, build XFDF from saved drawn or typed initials
        if (savedInitialsXfdf) {
          const xfdf = buildXfdfFromSaved(sig, savedInitialsXfdf);
          if (!xfdf) {
            return;
          }
          xfdfMap[sig.id] = xfdf;
        } else if (savedInitialsText) {
          // Build freetext XFDF for saved typed initials
          const textResult: SignatureResult = {
            type: 'text',
            text: savedInitialsText,
            fontFamily: savedInitialsFont || 'Dancing Script',
            saveSignature: false,
          };
          const built = buildXfdfForSig(sig, textResult);
          xfdfMap[sig.id] = built.xfdf;
        }
      }
    }

    try {
      await submitBatch(
        pendingSignatures,
        xfdfMap,
        null,
        undefined,
        '',
        'Dancing Script'
      );
    } catch {
      onSignError?.();
    }
  }, [
    pendingSignatures,
    buildXfdfFromSaved,
    buildXfdfForSig,
    submitBatch,
    savedSignatureXfdf,
    savedInitialsText,
    savedInitialsXfdf,
    savedInitialsFont,
    activeFsr,
    userId,
    onSignError,
  ]);

  const modalMode = useMemo(() => {
    const userSigs = filterUserSignatures(
      activeFsr.needed_signatures ?? [],
      userId
    );
    const pendingIsrs = userSigs.filter((sig) =>
      pendingSignatures.some((ps) => ps.id === sig.id)
    );

    const signatureIsrs = pendingIsrs.filter((sig) => !sig.is_initials);
    const initialsIsrs = pendingIsrs.filter((sig) => sig.is_initials);

    // Determine modal mode based on what's needed and what's missing
    const needsSignature = signatureIsrs.length > 0;
    const needsInitials = initialsIsrs.length > 0;
    const hasSignature = !!savedSignatureXfdf || justCollectedSignature;
    const hasInitials =
      !!savedInitialsText || !!savedInitialsXfdf || justCollectedInitials;

    // If both needed but only one is missing, open modal for the missing one
    if (needsSignature && needsInitials) {
      if (!hasSignature && hasInitials) {
        return 'signature';
      }
      if (hasSignature && !hasInitials) {
        return 'initials';
      }
      // Both missing or both present - default to signature
      return 'signature';
    }

    // Only one type needed
    if (needsInitials) {
      return 'initials';
    }
    return 'signature';
  }, [
    activeFsr,
    userId,
    pendingSignatures,
    savedSignatureXfdf,
    savedInitialsText,
    savedInitialsXfdf,
    justCollectedSignature,
    justCollectedInitials,
  ]);

  const handleModalDone = useCallback(
    async (result: SignatureResult) => {
      setModalOpen(false);

      const targets = signAllPending
        ? pendingSignatures
        : (() => {
            const single = pendingSignatures[0];
            return single ? [single] : [];
          })();

      if (targets.length === 0) {
        return;
      }

      // Get ISR details for targets
      const userSigs = filterUserSignatures(
        activeFsr.needed_signatures ?? [],
        userId
      );
      const targetIsrs = userSigs.filter((sig) =>
        targets.some((ps) => ps.id === sig.id)
      );

      const initialsToSign = targetIsrs.filter((sig) => sig.is_initials);
      const signaturesToSign = targetIsrs.filter((sig) => !sig.is_initials);

      // Determine what the modal was collecting based on modalMode
      const wasCollectingSignature = modalMode === 'signature';
      const wasCollectingInitials = modalMode === 'initials';

      // Build xfdfMap based on what we collected
      const xfdfMap: Record<number, string> = {};
      let standaloneXfdf: string | null = null;
      let initialsText = '';
      let initialsFontFamily = 'Dancing Script';

      if (wasCollectingSignature && signaturesToSign.length > 0) {
        // We just collected a signature, apply it to signature boxes
        for (const sig of targets) {
          const isr = targetIsrs.find((i) => i.id === sig.id);
          if (isr && !isr.is_initials) {
            const built = buildXfdfForSig(sig, result);
            xfdfMap[sig.id] = built.xfdf;
            if (!standaloneXfdf) {
              standaloneXfdf = built.standaloneXfdf;
            }
          }
        }

        // Mark that we collected a signature and store it
        setJustCollectedSignature(true);
        setPendingSignatureXfdf(standaloneXfdf);

        // Save signature if requested
        if (result.saveSignature && standaloneXfdf) {
          try {
            await saveSavedSignature(standaloneXfdf);
          } catch {
            // Non-critical
          }
        }
      }

      if (wasCollectingInitials && initialsToSign.length > 0) {
        if (result.type === 'text') {
          // Typed initials - build freetext XFDF for each initials box
          initialsText = result.text;
          initialsFontFamily = result.fontFamily;

          // Mark that we collected initials and store them
          setJustCollectedInitials(true);
          setPendingInitialsData({
            text: result.text,
            font: result.fontFamily,
          });

          // Build freetext XFDF for each initials signature
          for (const sig of targets) {
            const isr = targetIsrs.find((i) => i.id === sig.id);
            if (isr && isr.is_initials) {
              const built = buildXfdfForSig(sig, result);
              xfdfMap[sig.id] = built.xfdf;
            }
          }

          // Save initials if requested
          if (result.saveSignature) {
            try {
              await axios.put('signature/saved_initials/', {
                initials_text: result.text,
                font_family: result.fontFamily,
              });
              dispatch(
                setSavedInitials({
                  text: result.text,
                  font: result.fontFamily,
                })
              );
            } catch {
              // Non-critical
            }
          }
        } else if (result.type === 'ink') {
          // Drawn initials - build real XFDF just like signatures
          for (const sig of targets) {
            const isr = targetIsrs.find((i) => i.id === sig.id);
            if (isr && isr.is_initials) {
              const built = buildXfdfForSig(sig, result);
              xfdfMap[sig.id] = built.xfdf;
              if (!standaloneXfdf) {
                standaloneXfdf = built.standaloneXfdf;
              }
            }
          }

          // Save drawn initials if requested
          if (result.saveSignature && standaloneXfdf) {
            try {
              await axios.put('signature/saved_initials/', {
                signature_data: standaloneXfdf,
              });
              dispatch(setSavedInitials({ xfdf: standaloneXfdf }));
            } catch {
              // Non-critical
            }
          }

          // Mark that we collected drawn initials
          setJustCollectedInitials(true);
        }
      }

      // Now check if we need to collect the OTHER type
      const needsSignature = signaturesToSign.length > 0;
      const needsInitials = initialsToSign.length > 0;
      const hasSignature =
        !!savedSignatureXfdf ||
        justCollectedSignature ||
        (wasCollectingSignature && standaloneXfdf);
      const hasInitials =
        !!savedInitialsText ||
        !!savedInitialsXfdf ||
        justCollectedInitials ||
        (wasCollectingInitials && initialsText);

      if (needsSignature && needsInitials) {
        // Document needs both types
        if (wasCollectingSignature && !hasInitials) {
          // We just collected signature, but still need initials
          setModalOpen(true);
          return;
        }
        if (wasCollectingInitials && !hasSignature) {
          // We just collected initials, but still need signature
          setModalOpen(true);
          return;
        }
      }

      // Add any already-saved or just-collected items to xfdfMap
      if (needsSignature && !wasCollectingSignature) {
        // Use saved signature or pending signature for signature boxes
        const signatureXfdf = savedSignatureXfdf || pendingSignatureXfdf;
        if (signatureXfdf) {
          for (const sig of targets) {
            const isr = targetIsrs.find((i) => i.id === sig.id);
            if (isr && !isr.is_initials && !xfdfMap[sig.id]) {
              const xfdf = buildXfdfFromSaved(sig, signatureXfdf);
              if (xfdf) {
                xfdfMap[sig.id] = xfdf;
              }
            }
          }
        }
      }

      if (needsInitials && !wasCollectingInitials) {
        // Use saved initials (XFDF or text) or pending initials for initials boxes
        if (savedInitialsXfdf) {
          // Use saved drawn initials XFDF
          for (const sig of targets) {
            const isr = targetIsrs.find((i) => i.id === sig.id);
            if (isr && isr.is_initials && !xfdfMap[sig.id]) {
              const xfdf = buildXfdfFromSaved(sig, savedInitialsXfdf);
              if (xfdf) {
                xfdfMap[sig.id] = xfdf;
              }
            }
          }
        } else {
          // Use saved typed initials or pending typed initials
          const initialsData =
            pendingInitialsData ||
            (savedInitialsText
              ? {
                  text: savedInitialsText,
                  font: savedInitialsFont || 'Dancing Script',
                }
              : null);
          if (initialsData) {
            // Build freetext XFDF for typed initials
            const textResult: SignatureResult = {
              type: 'text',
              text: initialsData.text,
              fontFamily: initialsData.font,
              saveSignature: false,
            };
            for (const sig of targets) {
              const isr = targetIsrs.find((i) => i.id === sig.id);
              if (isr && isr.is_initials && !xfdfMap[sig.id]) {
                const built = buildXfdfForSig(sig, textResult);
                xfdfMap[sig.id] = built.xfdf;
              }
            }
          }
        }
      }

      // Submit the batch
      await submitBatch(
        targets,
        xfdfMap,
        null,
        result,
        initialsText,
        initialsFontFamily
      );

      // Reset the flags and pending data
      setJustCollectedSignature(false);
      setJustCollectedInitials(false);
      setPendingSignatureXfdf(null);
      setPendingInitialsData(null);
      setSignAllPending(false);
    },
    [
      signAllPending,
      pendingSignatures,
      buildXfdfForSig,
      buildXfdfFromSaved,
      submitBatch,
      activeFsr,
      userId,
      dispatch,
      modalMode,
      savedSignatureXfdf,
      savedInitialsText,
      savedInitialsXfdf,
      savedInitialsFont,
    ]
  );

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSignAllPending(false);
    setJustCollectedSignature(false);
    setJustCollectedInitials(false);
    setPendingSignatureXfdf(null);
    setPendingInitialsData(null);
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      hasSavedSignature: () => !!savedSignatureXfdf,
      signAll: (autoSign: boolean) => {
        if (pendingSignatures.length === 0 || !containerRef.current) {
          return;
        }

        const firstSig = pendingSignatures[0];
        const pageEl = containerRef.current.querySelector(
          `[data-page-index="${firstSig.pageNumber - 1}"]`
        );
        if (pageEl) {
          pageEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        if (autoSign) {
          void applyAutoSignAllWithInitials();
        } else {
          setSignAllPending(true);
          setModalOpen(true);
        }
      },
    }),
    [savedSignatureXfdf, pendingSignatures, applyAutoSignAllWithInitials]
  );

  return (
    <Box
      sx={{
        height: '100%',
        position: 'relative',
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          height: '100%',
          overflow: 'auto',
          backgroundColor: 'text.secondary',
        }}
      >
        {loadError && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              p: 3,
            }}
          >
            <Card sx={{ width: '100%', maxWidth: 400 }}>
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Failed to load document
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  We couldn&apos;t load this document. Please try again.
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => {
                    setLoadError(false);
                    setLoading(true);
                    setRetryCount((c) => c + 1);
                  }}
                >
                  Retry
                </Button>
              </CardContent>
            </Card>
          </Box>
        )}

        {pages.length > 0 && (
          <Box sx={{ py: 1 }}>
            {pages.map((page, idx) => {
              const pageNumber = idx + 1;
              const pageSigs = mappedSignatures.filter(
                (s) =>
                  s.pageNumber === pageNumber &&
                  (!s.completed || signedMap[s.id] !== undefined)
              );

              return (
                <PdfPageRenderer
                  key={idx}
                  page={page}
                  scale={effectiveScale}
                  pageIndex={idx}
                >
                  {pageSigs.map((sig) => {
                    const localSign = signedMap[sig.id];
                    const isSigned = localSign !== undefined || sig.completed;

                    return (
                      <SignatureBoxOverlay
                        key={sig.id}
                        x={sig.x}
                        y={sig.y}
                        width={sig.width}
                        height={sig.height}
                        scale={effectiveScale}
                        signed={isSigned}
                        signedPreviewSvg={localSign?.ink}
                        signedText={localSign?.text}
                        isInitials={sig.isInitials}
                      />
                    );
                  })}
                </PdfPageRenderer>
              );
            })}
          </Box>
        )}
      </Box>

      {pages.length > 0 && !loading && (
        <ZoomControls zoom={zoom} onChange={setZoom} />
      )}

      {(loading || isSaving) && (
        <Stack
          alignItems="center"
          justifyContent="center"
          gap={2}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
          }}
        >
          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
            {loading ? 'Loading document...' : 'Saving signatures...'}
          </Typography>
          <CircularProgress size={48} sx={{ color: '#fff' }} />
        </Stack>
      )}

      <SignaturePadModal
        open={modalOpen}
        onClose={handleModalClose}
        onDone={handleModalDone}
        userName={displayName}
        hasSavedSignature={!!savedSignatureXfdf}
        hasSavedInitials={!!savedInitialsText || !!savedInitialsXfdf}
        mode={modalMode}
      />
    </Box>
  );
};

export default PdfSignViewer;
